import {
  MouseEvent,
  SyntheticEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {useWindowInnerHeight} from '../../../common/shared/hooks/useWindowInnerHeight';
import {twMerge} from '../../../common/twMerge';
import {MediaElementReadyStateType} from '../../../domain/content/[seriesId]/viewer/[productId]/type/MediaElementReadyStateType.enum';
import {TalkMediaStatus} from '../../../domain/content/[seriesId]/viewer/[productId]/type/TalkMediaStatus.enum';
import {TalkPosition} from '../../../domain/content/[seriesId]/viewer/[productId]/type/TalkPosition.enum';
import {TalkShapeType} from '../../../domain/content/[seriesId]/viewer/[productId]/type/TalkShapeType.enum';
import {TalkType} from '../../../domain/content/[seriesId]/viewer/[productId]/type/TalkType.enum';
import {VideoPlayType} from '../../../domain/content/[seriesId]/viewer/[productId]/type/VIdeoPlayType.enum';
import {ZIndex} from '../../../domain/content/[seriesId]/viewer/[productId]/type/ZIndex.enum';
import {formatTalkViewerMediaTime} from '../../../domain/content/[seriesId]/viewer/[productId]/util/formatTalkViewerMediaTime';
import {getDataUrlOrUrl} from '../../../domain/content/[seriesId]/viewer/[productId]/util/getDataUrlOrUrl';
import {setMediaCurrentTime} from '../../../domain/content/[seriesId]/viewer/[productId]/util/setMediaCurrentTime';
import {Icon} from '../../atoms/Icon/Icon';
import {IconType} from '../../atoms/Icon/IconType.enum';
import {InternalStaticImageType} from '../../atoms/StaticImage/InternalStaticImageType.enum';
import {StaticImage} from '../../atoms/StaticImage/StaticImage';
import {Text} from '../../atoms/Text/Text';
import {TalkShape} from '../TalkShape/TalkShape';
import FullscreenContoller from './FullscreenController';
import {TalkVideoProps} from './TalkVideoProps.interface';

export function TalkVideo({
  media,
  mediaStatus,
  preloadedMedia,
  isFullscreen,
  muted,
  isMobile,
  videoPlayType,
  needMediaPlayedDirect,
  onClickCloseFullscreen: _onClickCloseFullscreen,
  onVideoEnded,
  changeVideoMediaStatus: _changeVideoMediaStatus,
  toggleMuted: _toggleMuted,
  talkColor,
  talkPosition,
  talkShapeType,
  talkType,
  characterName,
  characterNameColor,
}: TalkVideoProps) {
  const {
    source: {
      totalTime = 0,
      width: videoWidth = 0,
      height: videoHeight = 0,
      url,
      alt = '비디오',
      thumbnail,
    },
  } = media;
  const [videoDuration, setVideoDuration] = useState(totalTime);
  const [currentTime, setCurrentTime] = useState(0);
  const [isShowControls, setIsShowControls] = useState(false);
  const [isBuffering, setIsBuffering] = useState(false);
  const isPlaying = mediaStatus === TalkMediaStatus.Play;
  const videoRef = useRef<HTMLVideoElement>(null);

  const remainTime = useMemo(
    () => videoDuration - currentTime,
    [videoDuration, currentTime]
  );

  // 인라인 동영상 우상단에 남은 시간 보여줄 지 정하는 값. 동영상 재생 3초 이내일 때에만 보여준다.
  const isShowRemainTime = useMemo(() => {
    if (!videoRef.current) {
      return false;
    }
    return videoDuration - 3 < remainTime;
  }, [remainTime, videoDuration]);

  useEffect(() => {
    if (!videoRef.current) {
      return;
    }
    if (mediaStatus === TalkMediaStatus.Play) {
      if (needMediaPlayedDirect) {
        return;
      }
      // 톡뷰어의 비디오는 preload 타입을 모두 none으로 해 두었기 때문에, 비디오 렌더와 동시에 재생 시도시에 비디오 정보가 아예 없을 수 있다.
      // 이 경우 재생 시도 시 동영상 로드도 되지 않고 재생도 되지 않는 비정상적인 상태가 되기 때문에, 한 번 체크해서 데이터가 없는 경우는 load를 한 번 해 준다.
      if (
        videoRef.current.readyState === MediaElementReadyStateType.HAVE_NOTHING
      ) {
        videoRef.current.load();
      }
      videoRef.current.play();
    } else if (mediaStatus === TalkMediaStatus.Stop) {
      /**
       * 비디오를 pause시켜놓으면, 동영상은 멈춘 상태로 해당 동영상 파일에 대한 요청은 계속 진행 된다.
       * 즉, 여러 개의 고용량 동영상을 연달아 실행/정지 시키면서 톡을 진행하면 비디오 파일 요청 커넥션이 여러 개 유지 되고,
       * 뒤쪽 동영상들은 이 연결이 모두 해소될 때 까지 재생되지 않는 문제가 발생한다.
       * 따라서 여기서 source를 초기화해서 새로 Load 해 주는 것으로 정지를 대체한다.
       */
      videoRef.current.src = '';
      videoRef.current.src = url;
      videoRef.current.load();
      // 비디오가 중지된 경우 currentTime 초기화
      setCurrentTime(0);
    } else if (mediaStatus === TalkMediaStatus.Pause) {
      videoRef.current.pause();
    } else if (!thumbnail) {
      videoRef.current.load();
    }
  }, [mediaStatus, thumbnail, url, needMediaPlayedDirect]);

  // 뷰어 전체 음소거 옵션에 따라 비디오 소리 조절
  useEffect(() => {
    if (!videoRef.current) {
      return;
    }
    videoRef.current.muted = muted;
  }, [muted]);

  useEffect(() => {
    setIsShowControls(false);
  }, [isFullscreen]);

  // 비디오 현재시간 업데이트
  const timeUpdateHandler = useCallback(
    (e: SyntheticEvent<HTMLVideoElement>) => {
      const video = e.target as HTMLVideoElement;
      setCurrentTime(video.currentTime);
      setVideoDuration(video.duration);
    },
    []
  );

  const mutedIcon = muted ? IconType.SoundOff : IconType.SoundOn;

  const toggleShowControls = useCallback(
    (e: MouseEvent) => {
      e.stopPropagation();
      setIsShowControls(!isShowControls);
    },
    [isShowControls]
  );

  const startBuffering = useCallback(() => {
    setIsBuffering(true);
  }, []);

  const endBuffering = useCallback(() => {
    setIsBuffering(false);
  }, []);

  // 웹에서의 비디오 클릭 핸들러. 동영상 재생과는 별개로 나머지 처리는 모두 필요하므로 이벤트 버블링은 막지 않음
  const onClickVideo = useCallback(() => {
    if (!videoRef.current || !needMediaPlayedDirect) {
      return;
    }
    videoRef.current.paused && videoRef.current?.play();
  }, [needMediaPlayedDirect]);

  useEffect(() => {
    if (!isFullscreen) {
      return;
    }
    function visibilityChangeHandler() {
      if (!videoRef.current) {
        return;
      }
      if (document.visibilityState === 'visible') {
        setMediaCurrentTime(videoRef.current, currentTime);
      }
    }
    document.addEventListener('visibilitychange', visibilityChangeHandler);

    return () =>
      document.removeEventListener('visibilitychange', visibilityChangeHandler);
  }, [isFullscreen, currentTime]);

  const videoRatio = videoHeight / videoWidth;
  const innerHeight = useWindowInnerHeight();
  const resizeTimer = useRef(0);

  /**
   * innerHeight가 변하는 경우는 보통 가로 -> 세로모드 변환 등 orientation change가 일어났을 때이다.
   * 단, ios의 경우 모드변환 시 애니메이션이 발생하고, resize 이벤트는 애니메이션이 끝나기 전 발생해서 모드변환 후 innerHeight값이 실제 innerHeight와 크기가 다른 경우가 발생한다.
   * 그래서 풀스크린일 때 이런 경우 문제가 될 수 있으므로 innerHeight가 변한 뒤 얼마간 innerHeight의 변화를 감지해 resize 이벤트 발생 없이 innerHeight가 변했으면 resize이벤트를 발생시켜준다.
   */
  useEffect(() => {
    if (isFullscreen) {
      let startTime = 0;
      function observeInnerHeight(timestamp: number) {
        if (!startTime) {
          startTime = timestamp;
        }
        // 대부분 기기가 0.5초 내에 rotate가 완전히 종료되어 넉넉하게 1초만 확인하고, 변경 없으면 로직 종료
        if (timestamp >= startTime + 1000) {
          return;
        }
        if (innerHeight !== window.innerHeight) {
          window.dispatchEvent(new Event('resize'));
          return;
        }
        resizeTimer.current = window.requestAnimationFrame(observeInnerHeight);
      }
      resizeTimer.current = window.requestAnimationFrame(observeInnerHeight);
    }
    return () => window.cancelAnimationFrame(resizeTimer.current);
  }, [innerHeight, isFullscreen]);

  const thumbnailElement = useMemo(
    () =>
      thumbnail ? (
        <img
          className="absolute top-0 left-0 h-full w-full object-cover"
          src={getDataUrlOrUrl(thumbnail, preloadedMedia)}
          alt="썸네일"
        />
      ) : null,
    [preloadedMedia, thumbnail]
  );

  const thumbnailControllerElemet = useMemo(
    () => (
      <div className="absolute top-1/2 left-1/2 flex -translate-x-1/2 -translate-y-1/2 flex-col">
        <Icon type={IconType.BtnVideoPlay} className="h-75tvs w-66tvs" />
        <Text className="mx-auto mt-10tvs block min-w-62tvs text-24tvs text-static-mono-white">
          {formatTalkViewerMediaTime(totalTime)}
        </Text>
      </div>
    ),
    [totalTime]
  );

  const dimmedElement = useMemo(
    () => (
      <div className="absolute top-0 left-0 h-full w-full bg-static-mono-black opacity-20" />
    ),
    []
  );

  const toggleMuted = useCallback(
    (e: MouseEvent) => {
      e.stopPropagation();
      _toggleMuted();
    },
    [_toggleMuted]
  );

  const changeVideoMediaStatus = useCallback(
    (status: TalkMediaStatus) => {
      if (needMediaPlayedDirect) {
        videoRef.current?.play();
      }
      _changeVideoMediaStatus(status);
    },
    [needMediaPlayedDirect, _changeVideoMediaStatus]
  );

  const onClickCloseFullscreen = useCallback(
    (e: MouseEvent) => {
      e.stopPropagation();
      _onClickCloseFullscreen();
    },
    [_onClickCloseFullscreen]
  );

  const isNotSupportVideo = videoPlayType === VideoPlayType.None;
  // 웹에서 비디오를 띄우는 경우가 아니면 썸네일만 보여주고, 알맞는 안내문구를 보여준다.
  if (videoPlayType !== VideoPlayType.Web) {
    return (
      <TalkShape
        className={twMerge(
          talkShapeType === TalkShapeType.Bubble &&
            BUBBLE_TALK_TYPE_STYLE[talkType],
          talkType === TalkType.Omniscient &&
            talkPosition === TalkPosition.Center &&
            'max-w-660tvs'
        )}
        talkShapeType={talkShapeType}
        characterName={characterName}
        characterNameColor={characterNameColor}
        talkPosition={talkPosition}
        talkColor={
          talkShapeType === TalkShapeType.Box ? talkColor : 'transparent'
        }
      >
        <div
          // eslint-disable-next-line tailwindcss/no-custom-classname
          className={twMerge(
            'talk-video-container',
            'relative overflow-hidden',
            talkShapeType === TalkShapeType.Box &&
              talkType !== TalkType.Omniscient &&
              'w-430tvs',
            TALK_SHAPE_TYPE_VIDEO_STYLE[talkShapeType]
          )}
          style={{
            zIndex: 'inherit',
          }}
        >
          {thumbnailElement}
          {dimmedElement}
          {isNotSupportVideo && (
            <Text className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 whitespace-nowrap text-26tvs text-static-mono-white">
              {NOT_SUPPORT_VIDEO_TEXT}
            </Text>
          )}
          {!isNotSupportVideo && thumbnailControllerElemet}
        </div>
      </TalkShape>
    );
  }
  return (
    <TalkShape
      className={twMerge(
        talkShapeType === TalkShapeType.Bubble &&
          BUBBLE_TALK_TYPE_STYLE[talkType],
        talkType === TalkType.Omniscient &&
          talkPosition === TalkPosition.Center &&
          'w-660tvs',
        isFullscreen && 'w-full max-w-full p-0'
      )}
      talkShapeType={talkShapeType}
      characterName={characterName}
      characterNameColor={characterNameColor}
      talkPosition={talkPosition}
      talkColor={
        talkShapeType === TalkShapeType.Box ? talkColor : 'transparent'
      }
    >
      <div
        // 톡 상태(인라인 비디오)에서 클릭 동작은 saga가 담당한다. 풀스크린으로 떠 있을 때에는 톡을 클릭하는 게 아니므로 이 컴포넌트에서 처리하고 이벤트 버블링을 막는다
        onClick={isFullscreen ? toggleShowControls : onClickVideo}
        // eslint-disable-next-line tailwindcss/no-custom-classname
        className={twMerge(
          !isFullscreen && 'talk-video-container',
          !isFullscreen && [
            'relative overflow-hidden',
            talkShapeType === TalkShapeType.Box &&
              talkType !== TalkType.Omniscient &&
              'w-430tvs',
            TALK_SHAPE_TYPE_VIDEO_STYLE[talkShapeType],
          ],
          isFullscreen && [
            'relative box-border flex w-full flex-col items-center justify-center overflow-hidden bg-static-mono-black',
            'pr-safe pl-safe',
          ]
        )}
        style={
          !isFullscreen
            ? {
                zIndex: 'inherit',
              }
            : {
                zIndex: ZIndex.FullScreen,
                height: innerHeight,
              }
        }
      >
        <video
          className={twMerge(
            'w-full',
            isFullscreen && 'max-h-full',
            !isFullscreen && [
              'left-0 top-0 box-border h-full object-cover',
              videoRatio ? 'absolute' : 'static',
            ]
          )}
          style={
            !isFullscreen
              ? {
                  background: thumbnail
                    ? `url(${getDataUrlOrUrl(thumbnail, preloadedMedia)})`
                    : 'transparent',
                  backgroundSize: 'cover',
                }
              : undefined
          }
          ref={videoRef}
          src={url}
          controls={false}
          onTimeUpdate={timeUpdateHandler}
          onEnded={onVideoEnded}
          // 예기치 않게 canPlayThrough가 실행되지 않는 경우가 있어, 실제 play시에도 buffering이 꺼지도록 추가했다.
          onPlay={endBuffering}
          title={alt}
          muted
          playsInline={true}
          /**
           * 톡뷰어는 첫 렌더에 다수의 미디어 파일이 렌더될 수 있으므로, 여기에 preload 속성을 주게 되면
           * 한번에 많은 요청 연결이 생기게 될 가능성이 있고, 이 때 에러를 발생해 특정 기기에서 미디어의 정상적인 재생을 불가능하게 만든다.
           * 따라서 톡뷰어에 사용하는 미디어는 preload 속성을 모두 제거한다.
           */
          preload="none"
          onWaiting={startBuffering}
          onCanPlayThrough={endBuffering}
          onSeeking={startBuffering}
          onSeeked={endBuffering}
        />
        {/* 풀스크린이 아닐 때의 내부 UI  */}
        {!isFullscreen && isPlaying && (
          <div
            className={twMerge(
              `absolute top-24tvs right-24tvs
               box-border flex h-34tvs min-w-72tvs items-center justify-center rounded-8tvs
               px-4tvs text-22tvs font-medium text-[rgb(var(--colors-static-mono-white)_/_80%)] transition-opacity duration-500`,
              !isShowRemainTime && 'opacity-0'
            )}
            style={{
              textShadow: '0 0 6px rgba(0, 0, 0, 0.3)',
            }}
          >
            {formatTalkViewerMediaTime(remainTime)}
          </div>
        )}
        {!isFullscreen && !isPlaying && (
          <>
            {/* 정지 중 썸네일 이미지가 별도로 있는 경우에는 해당 이미지를 비디오 위에 씌워줌 */}
            {thumbnailElement}
            {dimmedElement}
            {thumbnailControllerElemet}
          </>
        )}
        {isFullscreen && videoRef.current && (
          <>
            <Icon
              onClick={onClickCloseFullscreen}
              type={IconType.BtnFullscreenVideoClose}
              className="absolute top-10tvs right-10tvs box-content h-50tvs w-50tvs p-20tvs"
              style={{
                zIndex: ZIndex.FullScreen + 1,
              }}
            />
            <Icon
              onClick={toggleMuted}
              type={mutedIcon}
              className={twMerge(
                'absolute right-39tvs bottom-39tvs h-64tvs w-64tvs opacity-60 mr-safe mb-safe',
                isShowControls && 'bottom-239tvs'
              )}
            />
            <FullscreenContoller
              videoElement={videoRef.current}
              changeVideoMediaStatus={changeVideoMediaStatus}
              mediaStatus={mediaStatus || TalkMediaStatus.Stop}
              isShowControls={isShowControls}
              isMobile={isMobile}
              currentTime={currentTime}
              videoDuration={videoDuration}
            />
          </>
        )}
        {isBuffering && isPlaying && (
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
            <StaticImage
              imageType={InternalStaticImageType.Spinner}
              className="h-72pxr w-72pxr"
            />
          </div>
        )}
      </div>
      <style jsx>
        {`
          .talk-video-container::after {
            padding-top: ${videoRatio * 100}%;
            display: block;
            content: '';
          }
        `}
      </style>
    </TalkShape>
  );
}

const NOT_SUPPORT_VIDEO_TEXT = '지원 불가한 기능입니다.';

const BUBBLE_TALK_TYPE_STYLE: {[key in TalkType]: string} = {
  [TalkType.Narration]: '',
  [TalkType.Omniscient]: 'w-567tvs',
  [TalkType.Talker]: 'w-426tvs',
  [TalkType.None]: '',
};

const TALK_SHAPE_TYPE_VIDEO_STYLE: {[key in TalkShapeType]: string} = {
  [TalkShapeType.Box]: 'rounded-8tvs',
  [TalkShapeType.Bubble]: 'rounded-26tvs',
  [TalkShapeType.None]: '',
};
